import { AccountProvider, Client } from '@collinsonx/constants/enums';
import {
  Consumer,
  LinkedAccount,
  Membership,
} from '@collinsonx/utils/generatedTypes/graphql';
import {
  DEFAULT_LANGUAGE_CODE,
  DEFAULT_PLATFORM,
  DEFAULT_VERSION,
  MOBILE_ACTIONS,
  PRODUCTION_DOMAIN,
} from 'config/constants';
import { BookingInfo, BookingWarn } from 'config/datadog';
import { STORAGE_NAMESPACE } from 'config/storage';
import dayjs from 'dayjs';
import { Maybe } from 'graphql/jsutils/Maybe';
import { BridgePayload } from 'types/booking';
import { clientIsReady } from 'utils/errorHandling';

import {
  AccountType,
  OriginType,
  sendInfoLog,
  sendWarnLog,
  useLogStorage,
} from './logging';

const consoleLogsDisabled =
  process.env.NEXT_PUBLIC_CONSOLE_LOGS_DISABLED === 'ON';

export const JSONParse = (value: null | string | undefined, fallback?: any) => {
  if (!clientIsReady) return null;

  if (typeof value !== 'string') return fallback || null;

  try {
    return value ? JSON.parse(value) : fallback || null;
  } catch (e) {
    sendWarnLog(BookingWarn.JSON_PARSE_FAILED, {
      origin: OriginType.App,
    });
  }

  return null;
};

export const getPayloadDefaultValues = () => {
  const defaultToken = {
    accountProvider: AccountProvider.PP,
    email: '',
    externalId: '',
    firstName: '',
    lastName: '',
    membershipNumber: '',
    membershipType: Client.None,
    sourceCode: '',
  };

  return {
    ...defaultToken,
    jwt: '',
    language: DEFAULT_LANGUAGE_CODE,
    loungeCode: '',
    platform: DEFAULT_PLATFORM,
    referrer: '#',
    token: defaultToken,
    version: process.env.NEXT_PUBLIC_VERSION || DEFAULT_VERSION,
  };
};

export const getStorageNamespace = (key: string) =>
  `${STORAGE_NAMESPACE}_${key}`;

export const getItem = (key: string) => {
  if (!clientIsReady) return null;

  try {
    return sessionStorage.getItem(getStorageNamespace(key));
  } catch (e) {
    sendWarnLog(BookingWarn.SESSION_STORAGE_GET_FAILED, {
      origin: OriginType.App,
    });
  }

  return null;
};

export const setItem = (key: string, value: string) => {
  if (!clientIsReady) return null;

  try {
    return sessionStorage.setItem(getStorageNamespace(key), value);
  } catch (e) {
    sendWarnLog(BookingWarn.SESSION_STORAGE_SET_FAILED, {
      origin: OriginType.App,
    });
  }

  return null;
};

export const getLocalStorageItem = (key: string) => {
  if (!clientIsReady) return null;

  try {
    return localStorage.getItem(getStorageNamespace(key));
  } catch (e) {
    sendWarnLog(BookingWarn.LOCAL_STORAGE_GET_FAILED, {
      origin: OriginType.App,
    });
  }

  return null;
};

export const setLocalStorageItem = (key: string, value: string) => {
  if (!clientIsReady) return null;

  try {
    return localStorage.setItem(getStorageNamespace(key), value);
  } catch (e) {
    sendWarnLog(BookingWarn.LOCAL_STORAGE_SET_FAILED, {
      origin: OriginType.App,
    });
  }

  return null;
};

export const removeItem = (key: string) => {
  try {
    return sessionStorage.removeItem(getStorageNamespace(key));
  } catch (e) {
    sendWarnLog(BookingWarn.SESSION_STORAGE_REMOVE_FAILED, {
      origin: OriginType.App,
    });
  }
};

export const log = (...args: any[]) => {
  if (consoleLogsDisabled) return;
  if (window?.location.host !== PRODUCTION_DOMAIN) console.log(...args);
};

export const sendMobileEvent = (value: MOBILE_ACTIONS | string) => {
  if (!clientIsReady) return;

  const { Android, webkit } = window as any;

  if (Android) {
    Android.onWebViewEvent(value);
  } else if (webkit) {
    webkit.messageHandlers.onWebViewEvent.postMessage?.(value);
  } else {
    sendWarnLog(BookingWarn.NO_MOBILE_ENV, {
      origin: OriginType.App,
    });
  }
};

type ValueT = Maybe<string> | string | undefined;
const { LK, PP, PP_PREFERRED } = AccountProvider;

const parse = (val: ValueT) => String(val ?? '');
const isSame = (val1: ValueT, val2: ValueT) => parse(val1) === parse(val2);

export const isPayloadValid = ({
  accountProvider,
  externalId,
  membershipNumber,
  sourceCode,
}: Partial<BridgePayload> = {}) => {
  const { setAccountType } = useLogStorage();

  if (membershipNumber && !sourceCode)
    setAccountType(AccountType.MEMBERSHIP_NUM);
  else if (membershipNumber && sourceCode)
    setAccountType(AccountType.MEMBERSHIP_NUM_AND_SC);
  else if (externalId && sourceCode)
    setAccountType(AccountType.EXTERNAL_ID_AND_SC);
  else setAccountType(AccountType.UNHANDLED);

  const hasValidNPDAccount = Boolean(
    sourceCode ? sourceCode && membershipNumber : membershipNumber
  );

  const hasValidMigratedNPDAccount = Boolean(membershipNumber && sourceCode);

  const hasValidAssociateAccount = Boolean(externalId && sourceCode);

  const hasValidAmExAccount = Boolean(membershipNumber && sourceCode);

  const hasValidAccountDetails = Boolean(
    hasValidNPDAccount ||
      hasValidMigratedNPDAccount ||
      hasValidAssociateAccount ||
      hasValidAmExAccount
  );

  const isValidAccountProvider = Boolean(
    accountProvider === PP ||
      accountProvider === LK ||
      accountProvider === PP_PREFERRED
  );

  return Boolean(isValidAccountProvider && hasValidAccountDetails);
};

export const findLinkedAccount = (
  payload: BridgePayload,
  linkedAccounts: LinkedAccount[] = []
) => {
  const matchedAmExAccount = linkedAccounts.find((account: LinkedAccount) =>
    Boolean(
      isSame(account.provider, payload.accountProvider) &&
        isSame(account.membershipID, payload.membershipNumber) &&
        isSame(account.sourceCode, payload.sourceCode)
    )
  );

  const matchedAssociateAccount = linkedAccounts.find(
    (account: LinkedAccount) =>
      Boolean(
        isSame(account.provider, payload.accountProvider) &&
          isSame(account.externalID, payload.externalId) &&
          isSame(account.sourceCode, payload.sourceCode)
      )
  );

  const matchedMigratedNPDAccount = linkedAccounts.find(
    (account: LinkedAccount) =>
      Boolean(
        isSame(account.provider, payload.accountProvider) &&
          isSame(account.membershipID, payload.membershipNumber) &&
          !account.sourceCode
      )
  );

  const matchedAccountDifferentSourceCode = linkedAccounts.find(
    (account: LinkedAccount) =>
      Boolean(
        isSame(account.provider, payload.accountProvider) &&
          isSame(account.membershipID, payload.membershipNumber) &&
          !isSame(account.sourceCode, payload.sourceCode)
      )
  );

  const accountFound = Boolean(
    matchedAmExAccount ||
      matchedAssociateAccount ||
      matchedMigratedNPDAccount ||
      matchedAccountDifferentSourceCode
  );

  if (!accountFound) return null;

  return (
    matchedAmExAccount ||
    matchedAssociateAccount ||
    matchedMigratedNPDAccount ||
    matchedAccountDifferentSourceCode
  );
};

export const getMembership = (
  payload: BridgePayload,
  memberships: Membership[] = []
) => {
  const { membershipNumber, sourceCode } = payload;
  const stringifiedMembershipNumber = membershipNumber?.toString() ?? '';
  const membershipNumberValues = sourceCode
    ? [
        `${sourceCode}_${stringifiedMembershipNumber}`,
        stringifiedMembershipNumber,
      ]
    : [stringifiedMembershipNumber];

  const membershipFound = memberships.find((membership) =>
    membershipNumberValues.includes(membership.membershipNumber.toString())
  );

  if (!membershipFound) return undefined;

  if (!sourceCode)
    sendInfoLog(BookingInfo.MEMBERSHIP_FOUND_WITHOUT_SOURCE_CODE, {
      membership: membershipFound,
      membershipNumber,
    });

  return membershipFound;
};

const membershipPrefixMatchesSourceCode = (
  membership: Membership,
  sourceCode: string
) => {
  const prefix = membership.membershipPlan?.membershipPrefix;
  if (!prefix) return false;

  const withoutTrailingUnderscore = prefix.endsWith('_')
    ? prefix.slice(0, -1)
    : prefix;
  const withoutLastCharacter = prefix.slice(0, -1);

  return (
    prefix === sourceCode ||
    withoutTrailingUnderscore === sourceCode ||
    withoutLastCharacter === sourceCode
  );
};

// BridgePayload has no `membership` object. Matching by reference always missed
// an existing entitlements deal and re-called enrolLinkedAccountToAvailablePlans.
export const findEntitlementsDeal = (
  payload: BridgePayload,
  memberships: Membership[] = []
) => {
  const matchedByNumber = getMembership(payload, memberships);
  if (matchedByNumber) return matchedByNumber;

  const { sourceCode } = payload;
  if (!sourceCode) return undefined;

  return memberships.find((membership) =>
    membershipPrefixMatchesSourceCode(membership, sourceCode)
  );
};

export const parsePayload = (payload: BridgePayload): BridgePayload => {
  const membershipNumberSafeType = payload.membershipNumber?.toString() ?? '';

  // sourceCode_membershipNumber pattern is used only for email token
  const isEmailToken = membershipNumberSafeType.includes('_');

  if (!isEmailToken) return payload;

  sendInfoLog(BookingInfo.EMAIL_TOKEN_PROVIDED, {
    membershipNumber: membershipNumberSafeType,
  });

  const [sourceCode, membershipNumber] = membershipNumberSafeType.split('_');

  return {
    ...payload,
    membershipNumber,
    sourceCode,
  };
};

export const consumerIsValid = (consumer: Consumer) => {
  const { dateOfBirth, firstName, lastName } = consumer || {};
  return consumer && firstName && lastName && dateOfBirth;
};

export const checkHoursDiff = (
  startDate: Date | null,
  endDate: Date | null
) => {
  const date = dayjs(startDate);
  return date.diff(endDate, 'hour', true);
};

export const sanitizeConsumerData = (consumer: Partial<Consumer>) => ({
  createdAt: consumer.createdAt,
  id: consumer.id,
  linkedAccounts:
    consumer?.linkedAccounts?.map((account) => ({
      createdAt: account.createdAt,
      externalID: account.externalID,
      id: account.id,
      membershipID: account.membershipID,
      membershipType: account.membershipType,
      provider: account.provider,
      sourceCode: account.sourceCode,
      updatedAt: account.updatedAt,
    })) ?? [],
  locale: consumer.locale,
  memberships:
    consumer?.memberships?.map((membership) => ({
      createdAt: membership.createdAt,
      id: membership.id,
      membershipNumber: membership.membershipNumber,
      membershipStatus: membership.membershipStatus,
      updatedAt: membership.updatedAt,
      userEntitlements: membership.userEntitlements,
    })) ?? [],
  updatedAt: consumer.updatedAt,
});
